import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { AlertTriangle, Rocket } from "lucide-react"
import { type TestBill, type AIMonitorConfig } from "@/types/config"
import { TestBillsList } from "./test-bills-list"
import { useMonitorCostEstimate } from "@/hooks/bills/useMonitorCostEstimate"

interface ModeSettingsProps {
  testMode: boolean
  productionEnabled: boolean
  testBills: TestBill[]
  onUpdate: (config: Partial<AIMonitorConfig>) => void
  isLoading?: boolean
  session?: string
}

export function ModeSettings({ 
  testMode, 
  productionEnabled, 
  testBills,
  onUpdate,
  isLoading,
  session 
}: ModeSettingsProps) {
  const { data: costData } = useMonitorCostEstimate(session)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Test Mode
          </CardTitle>
          <CardDescription>
            Process only specified test bills
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
                id="test-mode-switch"
                checked={testMode}
                onCheckedChange={(checked) => onUpdate({TestMode: checked})}
                disabled={isLoading}
                aria-labelledby="test-mode-label"
            />
            <label
                htmlFor="test-mode-switch"
                id="test-mode-label"
                className="text-sm font-medium leading-none cursor-pointer"
            >
              Enable Test Mode
            </label>
          </div>
          {testMode && (
              <TestBillsList
                  bills={testBills}
                  onUpdate={(bills) => onUpdate({TestBills: bills})}
                  isLoading={isLoading}
                />
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-blue-500"/>
            Production Mode
          </CardTitle>
          <CardDescription>
            Automatically process all new bills
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Switch
                  id="production-mode-switch"
                  checked={productionEnabled}
                  onCheckedChange={(checked) => onUpdate({ProductionEnabled: checked})}
                  disabled={isLoading || testMode}
                  aria-labelledby="production-mode-label"
              />
              <label
                  htmlFor="production-mode-switch"
                  id="production-mode-label"
                  className="text-sm font-medium leading-none cursor-pointer"
              >
                Enable Production Mode
              </label>
            </div>
            <div className="text-sm text-muted-foreground">
              Estimated Cost: ${costData?.TotalUSD || '0.00'}
            </div>
          </div>
          {testMode && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Production mode unavailable</AlertTitle>
              <AlertDescription>
                Disable test mode to enable production monitoring
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  )
}